import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import classNames from "classnames";
import { SizeInformation } from "client_legacy/ndaw/sizing";
import {
	EffectiveTheme,
	useEffectiveTheme,
	useValueEqualMemo
} from "client/Hooks";
import {
	IFrame,
	IFramePermission,
	NoResults
} from "client/Components";
import { urlEncodeObject } from "client_legacy/ndaw/urlHelper";
import { getCurrentCulture, localize } from "client_legacy/ndaw/culture";
import styles from "./UserHtml.module.less";
import { ensureNotNil, setTrackingId } from "client/Misc/utils";
import { userConfiguration, UserHtmlModel } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";

export type UserHtmlSizeKind = "stretch" | "content-size";

export type UserHtmlSize = {
	horizontal: UserHtmlSizeKind;
	vertical: UserHtmlSizeKind;
};

export type UserHtmlProps = {
	html: string;
	size?: UserHtmlSizeKind | UserHtmlSize;
	trackingId: string;
};

export const UserHtml = (props: UserHtmlProps): ReactElement => {
	const {
		html,
		size = "content-size",
		trackingId
	} = props;

	const model = useMemo(
		(): UserHtmlModel => userConfiguration().userHtml,
		[]
	);

	const configuredSize: UserHtmlSize = useValueEqualMemo(
		(): UserHtmlSize => {
			return typeof size === "string"
				? {
					horizontal: size,
					vertical: size
				}
				: size;
		},
		[size]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.root,
				{
					[styles.root_stretchedX]: configuredSize.horizontal === "stretch",
					[styles.root_stretchedY]: configuredSize.vertical === "stretch"
				}
			)}
		>
			{!model.isConfigured
				? (
					<NoResults
						title={localize("_EmbedContentNotEnabled")}
						message={localize("_PleaseContactYourAdminToEnableEmbeddedContent")}
						height="fill"
						icon="gear"
						trackingId="not-configured"
					/>
				)
				: (
					<UserHtmlIframe
						configuredSize={configuredSize}
						hostUrl={model.url}
						html={html}
						trackingId="iframe"
					/>
				)
			}
		</div>
	);
};

UserHtml.displayName = "UserHtml";

type UserHtmlIframeProps = {
	hostUrl: string;
	html: string;
	configuredSize: UserHtmlSize;
	trackingId: string;
};

const UserHtmlIframe = (props: UserHtmlIframeProps): ReactElement => {
	const {
		hostUrl,
		html,
		configuredSize,
		trackingId
	} = props;

	const [contentSize, setContentSize] = useState<Partial<SizeInformation>>({});
	const ref: RefObject<HTMLIFrameElement | null> = useRef<HTMLIFrameElement>(null);
	const theme: EffectiveTheme = useEffectiveTheme();
	const forceReloadTrackerRef: RefObject<ForceReloadTrackerState | null> = useRef<ForceReloadTrackerState>(null);

	const { src, origin } = useMemo(
		(): { src: string; origin: string; } => {
			const queryString: string = urlEncodeObject({
				culture: getCurrentCulture(),
				theme: theme,
				height: configuredSize.vertical,
				width: configuredSize.horizontal
			});

			const url = new URL(hostUrl);
			url.search = `?${queryString}`;

			const src: string = url.toString();

			return {
				src,
				origin: url.origin
			};
		},
		[
			configuredSize.horizontal,
			configuredSize.vertical,
			hostUrl,
			theme
		]
	);

	const loadHandler = useCallback(
		(): void => {
			const { current: iframe } = ref;

			if (!iframe) {
				return;
			}

			const message: LoadHtmlMessage = {
				kind: "load-html",
				html
			};

			iframe.contentWindow
				?.postMessage(
					message,
					origin
				);
		},
		[origin, html]
	);

	useEffect(
		(): (() => void) => {
			const messageHandler = (args: MessageEvent) => {
				const { current: iframe } = ref;

				if (!iframe?.contentWindow) {
					return;
				}

				if (args.source !== iframe.contentWindow) {
					return;
				}

				if (args.origin !== origin) {
					return;
				}

				if (!args.data) {
					return;
				}

				let data: unknown;
				if (typeof args.data === "string") {
					try {
						data = JSON.parse(args.data);
					} catch (e) {
						console.error(e);
					}
				} else if (typeof args.data === "object") {
					data = args.data;
				} else {
					return;
				}

				if (!isSizeChangedMessage(data)) {
					return;
				}

				setContentSize({
					height: data.height,
					width: data.width
				});
			};

			window.addEventListener("message", messageHandler);
			return () => window.removeEventListener("message", messageHandler);
		},
		[origin]
	);

	useEffect(
		(): void => {
			// The implementation of this component relies on the onLoad event. When only the html
			// prop changes, it will not cause the iframe to load resulting in no onLoad event to fire.
			// So we trigger it ourself by setting the src attribute again.
			let shouldForceReload: boolean = false;

			if (forceReloadTrackerRef.current !== null) {
				const {
					src: previousSrc,
					html: previousHtml
				} = forceReloadTrackerRef.current;

				shouldForceReload = src === previousSrc && html !== previousHtml;
			}

			if (shouldForceReload) {
				ensureNotNil(ref.current)
					.src = src;
			}

			forceReloadTrackerRef.current = { src, html };
		},
		[html, src]
	);

	return (
		<IFrame
			src={src}
			ref={ref}
			style={{
				width: configuredSize.horizontal === "content-size"
					? contentSize.width
					: undefined,
				height: configuredSize.vertical === "content-size"
					? contentSize.height
					: undefined
			}}
			className={classNames(
				styles.iframe,
				{
					[styles.iframe_stretchedX]: configuredSize.horizontal === "stretch",
					[styles.iframe_stretchedY]: configuredSize.vertical === "stretch"
				}
			)}
			trackingId={trackingId}
			permissions={iframePermissions}
			onLoad={loadHandler}
		/>
	);
};
UserHtmlIframe.displayName = "UserHtmlIframe";

type LoadHtmlMessage = {
	kind: "load-html";
	html: string;
};

type SizeChangedMessage = {
	kind: "size-changed";
	height: number | undefined;
	width: number | undefined;
};

function isSizeChangedMessage(data: unknown): data is SizeChangedMessage {
	if (!data || typeof data !== "object") {
		return false;
	}

	const message = (data as SizeChangedMessage);

	// NOTE: strict validation because this data comes from an external system
	return message.hasOwnProperty("kind") && message.kind === "size-changed"
		&& message.hasOwnProperty("height") && (typeof message.height === "undefined" || typeof message.height === "number")
		&& message.hasOwnProperty("width") && (typeof message.width === "undefined" || typeof message.width === "number");
}

const iframePermissions: readonly IFramePermission[] = [
	IFramePermission.Downloads,
	IFramePermission.Forms,
	IFramePermission.Modals,
	IFramePermission.OrientationLock,
	IFramePermission.PointerLock,
	IFramePermission.Popups,
	IFramePermission.EscapingPopups,
	IFramePermission.Presentation,
	IFramePermission.SameOrigin,
	IFramePermission.Scripts
];

type ForceReloadTrackerState = {
	src: string;
	html: string;
};
